import { ChangeDetectionStrategy, Component, computed, forwardRef, input, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { FieldLabelComponent } from '../field-label/field-label.component';

type InputType = 'text' | 'email' | 'tel' | 'number' | 'url' | 'password' | 'date';

let idCounter = 0;
const nextId = (): string => `bx-field-input-${++idCounter}`;

/**
 * R-FORM-02/03/04/05 — single labeled text input with focus ring, inline error, suffix slot.
 *
 * - 44px min-height (R-FORM-02)
 * - Label above (R-FORM-03)
 * - `--focus-ring` on focus, `--focus-ring-error` on invalid (R-FORM-04)
 * - `aria-invalid`/`aria-describedby` + `role="alert"` error rendered below (R-FORM-05)
 *
 * Implements ControlValueAccessor so it can be driven by [formControl] / [(ngModel)].
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'bodyanalytics-field-input',
  standalone: true,
  imports: [FieldLabelComponent],
  templateUrl: './field-input.component.html',
  styleUrls: ['./field-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FieldInputComponent),
      multi: true,
    },
  ],
})
export class FieldInputComponent implements ControlValueAccessor {
  readonly label = input.required<string>();
  readonly type = input<InputType>('text');
  readonly placeholder = input<string>('');
  readonly required = input<boolean>(false);
  readonly hint = input<string | undefined>(undefined);
  readonly error = input<string | undefined>(undefined);
  readonly suffix = input<string | undefined>(undefined);
  readonly inputId = input<string>(nextId());
  readonly autocomplete = input<string | undefined>(undefined);
  readonly inputmode = input<'text' | 'numeric' | 'tel' | 'email' | 'decimal' | undefined>(undefined);

  /** Reactive Forms / ngModel value */
  protected readonly value = signal<string | number | null>(null);
  protected readonly disabled = signal(false);
  protected readonly touched = signal(false);

  protected readonly errorId = computed(() => `${this.inputId()}-error`);
  protected readonly describedBy = computed(() => (this.error() ? this.errorId() : null));
  protected readonly invalid = computed(() => !!this.error());

  private onChange: (value: string | number | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  // ── ControlValueAccessor ────────────────────────────────────────────────
  writeValue(value: string | number | null): void {
    this.value.set(value);
  }
  registerOnChange(fn: (value: string | number | null) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  // ── DOM event handlers ─────────────────────────────────────────────────
  protected handleInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const raw = target.value;
    const coerced: string | number | null = this.type() === 'number' ? (raw === '' ? null : Number(raw)) : raw;
    this.value.set(coerced);
    this.onChange(coerced);
  }

  protected handleBlur(): void {
    this.touched.set(true);
    this.onTouched();
  }
}
